import { useState, useCallback, useEffect, useRef } from 'react';
import { saveToFile, loadFromFile } from '../utils/fileHelpers';
import { normalizeCategory } from '../utils/categoryHelpers';
import {
  saveFile,
  updateLastOpenedFile,
  loadExpenseFile,
  deleteFile,
  listAllFiles,
  getLastOpenedFile,
  restoreLocalTx,
} from '../utils/firestoreStorage';
import { listReceivedReports } from '../utils/sharingStorage';

export const useFileOperations = (expenses, setExpenses, uid, imageStorage = true) => {
  const [saveMsg, setSaveMsg] = useState(null);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [savedFiles, setSavedFiles] = useState([]);
  const [sharedWithMeFiles, setSharedWithMeFiles] = useState([]);
  const [currentFileName, setCurrentFileName] = useState(null);
  const [currentFileReportType, setCurrentFileReportType] = useState('expense');
  const [currentFileOwnerUid, setCurrentFileOwnerUid] = useState(null); // null = own file
  const [currentFileAccess, setCurrentFileAccess] = useState(null); // null = own, 'view' | 'edit' = shared
  const [currentFileLocal, setCurrentFileLocal] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [filesLoaded, setFilesLoaded] = useState(false);
  const [lastFileLoaded, setLastFileLoaded] = useState(false);

  const autoSaveTimer = useRef(null);

  // Reset flags when uid changes so triggers fire correctly on each login
  useEffect(() => {
    setFilesLoaded(false);
    setLastFileLoaded(false);
  }, [uid]);

  // Auto-load last file when user logs in
  useEffect(() => {
    if (!uid) return;
    let cancelled = false;
    (async () => {
      try {
        const file = await getLastOpenedFile(uid);
        if (file && !cancelled) {
          setExpenses(file.data);
          setCurrentFileName(file.name);
          setCurrentFileReportType(file.reportType ?? 'expense');
          setCurrentFileLocal(file.local ?? false);
          setSaveMsg(`Loaded "${file.name}"`);
          setTimeout(() => setSaveMsg(null), 2000);
        }
      } catch (err) {
        console.error("Failed to auto-load last file:", err);
      }
      if (!cancelled) setLastFileLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [uid, setExpenses]);

  // Auto-save whenever expenses change, debounced 1.5s
  useEffect(() => {
    if (!currentFileName) return;

    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);

    autoSaveTimer.current = setTimeout(async () => {
      // View-only grantees must not write back to the owner's file
      if (currentFileOwnerUid && currentFileAccess !== 'edit') return;

      setAutoSaving(true);
      try {
        const normalizedExpenses = expenses.map(e =>
          e.category ? { ...e, category: normalizeCategory(e.category) } : e
        );
        if (uid || currentFileOwnerUid) {
          // Firestore: upload images to Storage, save doc.
          // For shared files opened by a grantee, save back to the owner's path
          // (the grantee is in sharedWith so security rules allow this write).
          // We intentionally do NOT call setExpenses here to avoid a re-save loop.
          // Base64 previews stay in memory and display fine; Storage URLs are used on next load.
          const saveUid = currentFileOwnerUid ?? uid;
          await saveFile(saveUid, currentFileName, normalizedExpenses, { imageStorage, reportType: currentFileReportType, local: currentFileLocal });
          if (!currentFileOwnerUid) {
            // Only track lastFile and update local cache for own files —
            // grantees must not write to the owner's meta/prefs.
            await updateLastOpenedFile(uid, currentFileName);
            setSavedFiles(prev => {
              const exists = prev.find(f => f.name === currentFileName);
              if (exists) return prev.map(f => f.name === currentFileName ? { ...f, data: normalizedExpenses } : f);
              return [...prev, { name: currentFileName, data: normalizedExpenses }];
            });
          }
        } else if (window.storage) {
          // Guest: persist to localStorage
          await window.storage.set("receipt_save_" + currentFileName, JSON.stringify(normalizedExpenses));
          const idxRes = await window.storage.get("receipt_index").catch(() => null);
          const idx = idxRes?.value ? JSON.parse(idxRes.value) : [];
          if (!idx.includes(currentFileName)) idx.push(currentFileName);
          await window.storage.set("receipt_index", JSON.stringify(idx));
        }
      } catch (err) {
        console.error("Auto-save failed:", err);
      } finally {
        setAutoSaving(false);
      }
    }, 1500);

    return () => clearTimeout(autoSaveTimer.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expenses, currentFileName, uid, currentFileOwnerUid, currentFileAccess, currentFileReportType, imageStorage, currentFileLocal]);

  const handleLoad = useCallback((file) => {
    if (file.data) {
      setExpenses(file.data);
      setCurrentFileName(file.name || null);
      setCurrentFileReportType(file.reportType ?? 'expense');
      setCurrentFileLocal(file.local ?? false);
      setSaveMsg(`Loaded "${file.name}"`);
      setTimeout(() => setSaveMsg(null), 1500);
      return Promise.resolve();
    }
    return loadFromFile(setExpenses, setSaveMsg);
  }, [setExpenses]);

  const handleDelete = useCallback(async (file) => {
    if (uid) {
      try {
        await deleteFile(uid, file.name, file.reportType);
        setSavedFiles(prev => prev.filter(f => f.name !== file.name));
        return;
      } catch (error) {
        console.error("Firestore delete failed:", error);
      }
    }
    try {
      if (!window.storage) return;
      await window.storage.delete(file.key);
      const idxRes = await window.storage.get("receipt_index").catch(() => null);
      const idx = idxRes?.value ? JSON.parse(idxRes.value) : [];
      await window.storage.set("receipt_index", JSON.stringify(idx.filter(n => n !== file.name)));
      setSavedFiles(prev => prev.filter(f => f.name !== file.name));
    } catch (error) {
      console.error("Delete failed:", error);
    }
  }, [uid]);

  const loadSharedWithMeFiles = useCallback(async () => {
    if (!uid) { setSharedWithMeFiles([]); return; }
    try {
      const received = await listReceivedReports(uid);
      setSharedWithMeFiles(received);
    } catch (error) {
      console.error("Failed to load shared-with-me files:", error);
      setSharedWithMeFiles([]);
    }
  }, [uid]);

  const handleLoadShared = useCallback(async (receivedReport) => {
    try {
      const file = await loadExpenseFile(receivedReport.ownerUid, receivedReport.filename);
      if (file) {
        setExpenses(file.data);
        setCurrentFileName(file.name);
        setCurrentFileReportType(file.reportType ?? 'expense');
        setCurrentFileOwnerUid(receivedReport.ownerUid);
        setCurrentFileAccess(receivedReport.access ?? 'edit');
        setSaveMsg(`Loaded "${file.name}" (shared by ${receivedReport.ownerEmail})`);
        setTimeout(() => setSaveMsg(null), 2000);
      }
    } catch (err) {
      console.error("Failed to load shared file:", err);
    }
  }, [setExpenses]);

  const loadSavedFiles = useCallback(async () => {
    if (uid) {
      try {
        const files = await listAllFiles(uid);
        setSavedFiles(files);
        loadSharedWithMeFiles();
        setFilesLoaded(true);
        return files;
      } catch (error) {
        console.error("Firestore list failed:", error);
        setFilesLoaded(true);
      }
    }
    try {
      if (!window.storage) { setSavedFiles([]); setFilesLoaded(true); return; }
      const idxRes = await window.storage.get("receipt_index").catch(() => null);
      const idx = idxRes?.value ? JSON.parse(idxRes.value) : [];
      const files = await Promise.all(idx.map(async name => {
        try {
          const res = await window.storage.get("receipt_save_" + name);
          return { key: "receipt_save_" + name, name, data: res ? JSON.parse(res.value) : [] };
        } catch { return null; }
      }));
      const filtered = files.filter(Boolean);
      setSavedFiles(filtered);
      setFilesLoaded(true);
      return filtered;
    } catch (error) {
      console.error("Failed to load saved files:", error);
      setSavedFiles([]);
    }
    setFilesLoaded(true);
  }, [uid, loadSharedWithMeFiles]);

  const patchSavedFile = useCallback((fileName, data) => {
    setSavedFiles(prev => prev.map(f => f.name === fileName ? { ...f, data } : f));
  }, []);

  const handleLocalExport = useCallback(() => {
    saveToFile(expenses, setSaveMsg);
  }, [expenses]);

  const handleRestoreLocalFile = useCallback((filename, pickedFile) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        try {
          const raw = JSON.parse(ev.target.result);
          const data = Array.isArray(raw) ? raw : (Array.isArray(raw.data) ? raw.data : []);
          await restoreLocalTx(uid, filename, data);
          resolve(data);
        } catch (e) { reject(e); }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(pickedFile);
    });
  }, [uid]);

  return {
    saveMsg,
    autoSaving,
    filesLoaded,
    lastFileLoaded,
    showLoadModal,
    setShowLoadModal,
    showExport,
    setShowExport,
    savedFiles,
    sharedWithMeFiles,
    currentFileName,
    setCurrentFileName,
    currentFileReportType,
    setCurrentFileReportType,
    currentFileOwnerUid,
    setCurrentFileOwnerUid,
    currentFileAccess,
    setCurrentFileAccess,
    currentFileLocal,
    setCurrentFileLocal,
    handleLoad,
    handleLoadShared,
    handleDelete,
    loadSavedFiles,
    patchSavedFile,
    handleLocalExport,
    handleRestoreLocalFile,
  };
};
